import * as React from 'react';
import propz from 'propz';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { createClubProgramme, getClubProgramme, updateClubProgramme } from 'Src/helpers/service/admin/clubProgrammes';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';
import { ClubProgrammeForm } from './ClubProgrammeForm';
import { ClubProgrammeFormValues } from './helpers/clubProgrammeForm';
import './ClubProgrammes.css';

type Props = {
  user: AppUser;
};

type BackLocation = {
  pathname: string;
  search?: string;
};

const sameDateTime = (a?: Date | null, b?: Date | null): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.getTime() === b.getTime();
};

const sameArray = (a: Array<string | number>, b: Array<string | number>): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const buildCreatePayload = (values: ClubProgrammeFormValues): any => ({
  name: values.name.trim(),
  description: values.description.trim(),
  booking: {
    opensAt: values.bookingOpensAt?.toISOString(),
    closesAt: values.bookingClosesAt?.toISOString(),
    preBookingAccess: values.preBookingAccess,
    allowChangesAfterSubmit: Boolean(values.allowChangesAfterSubmit)
  },
  selectionRules: {
    mode: values.selectionMode,
    priorityRange: {
      min: 1,
      max: Math.trunc(Number(values.priorityMax))
    }
  },
  audience: {
    ages: values.audienceAges.map(value => Math.trunc(Number(value))).filter(Number.isFinite),
    genders: values.audienceGenders,
    invitations: values.audienceInvitations
  }
});

const buildUpdatePayload = (programme: AfterSchoolClubProgramme, values: ClubProgrammeFormValues): any => {
  const patch: any = {};
  const bookingPatch: any = {};
  const initialName = String(programme?.name || '').trim();
  const initialDescription = String(programme?.description || '').trim();
  const initialOpensAt = programme?.booking?.opensAt ? new Date(programme.booking.opensAt) : null;
  const initialClosesAt = programme?.booking?.closesAt ? new Date(programme.booking.closesAt) : null;
  const initialPreBookingAccess = String(programme?.booking?.preBookingAccess || 'VIEW').toUpperCase();
  const initialAllowChanges = Boolean(programme?.booking?.allowChangesAfterSubmit);
  const initialSelectionMode = String(programme?.selectionRules?.mode || 'SINGLE').toUpperCase();
  const initialPriorityMax = Math.trunc(Number(programme?.selectionRules?.priorityRange?.max || 10));
  const initialAges = (Array.isArray(programme?.audience?.ages) ? programme.audience.ages : [])
    .map(value => Math.trunc(Number(value)))
    .filter(Number.isFinite);
  const initialGenders = Array.isArray(programme?.audience?.genders) ? programme.audience.genders : [];
  const initialInvitations = String(programme?.audience?.invitations || 'PARENT').toUpperCase();
  const nextAges = values.audienceAges.map(value => Math.trunc(Number(value))).filter(Number.isFinite);

  if (values.name.trim() !== initialName) patch.name = values.name.trim();
  if (values.description.trim() !== initialDescription) patch.description = values.description.trim();
  if (!sameDateTime(values.bookingOpensAt, initialOpensAt)) {
    bookingPatch.opensAt = values.bookingOpensAt?.toISOString();
  }
  if (!sameDateTime(values.bookingClosesAt, initialClosesAt)) {
    bookingPatch.closesAt = values.bookingClosesAt?.toISOString();
  }
  if (String(values.preBookingAccess).toUpperCase() !== initialPreBookingAccess) {
    bookingPatch.preBookingAccess = values.preBookingAccess;
  }
  if (Boolean(values.allowChangesAfterSubmit) !== initialAllowChanges) {
    bookingPatch.allowChangesAfterSubmit = Boolean(values.allowChangesAfterSubmit);
  }
  if (Object.keys(bookingPatch).length) patch.booking = bookingPatch;

  if (
    String(values.selectionMode).toUpperCase() !== initialSelectionMode ||
    Math.trunc(Number(values.priorityMax)) !== initialPriorityMax
  ) {
    patch.selectionRules = {
      mode: values.selectionMode,
      priorityRange: { min: 1, max: Math.trunc(Number(values.priorityMax)) }
    };
  }

  if (
    !sameArray(nextAges, initialAges) ||
    !sameArray(values.audienceGenders, initialGenders) ||
    String(values.audienceInvitations).toUpperCase() !== initialInvitations
  ) {
    patch.audience = {
      ages: nextAges,
      genders: values.audienceGenders,
      invitations: values.audienceInvitations
    };
  }

  return patch;
};

export const ClubProgrammeEditorPage: React.FC<Props> = ({ user }) => {
  const { programmeId } = useParams<{ programmeId?: string }>();
  const history = useHistory();
  const location = useLocation<{ from?: BackLocation }>();
  const isEditMode = Boolean(programmeId);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [programme, setProgramme] = React.useState<AfterSchoolClubProgramme | undefined>();
  const [forms, setForms] = React.useState<any[]>([]);
  const [isDirty, setIsDirty] = React.useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [backendOpensError, setBackendOpensError] = React.useState('');
  const [backendClosesError, setBackendClosesError] = React.useState('');
  const [backendFormError, setBackendFormError] = React.useState('');
  const backLocation = React.useMemo<BackLocation>(
    () => location.state?.from || { pathname: '/clubProgrammes', search: '' },
    [location.state]
  );

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError('');

    Promise.all([
      getAllForms(user),
      isEditMode && programmeId ? getClubProgramme(user, String(programmeId)) : Promise.resolve(undefined)
    ])
      .then(([loadedForms, loadedProgramme]) => {
        if (cancelled) return;
        setForms(Array.isArray(loadedForms) ? loadedForms : []);
        setProgramme(loadedProgramme as AfterSchoolClubProgramme | undefined);
        setIsLoading(false);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('ClubProgrammeEditorPage load error:', error);
        setLoadError('Unable to load the programme form. Please try again.');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEditMode, programmeId, user]);

  React.useEffect(() => {
    const action = isEditMode ? 'Edit programme' : 'Create programme';
    window.dispatchEvent(
      new CustomEvent('sit:topbar-breadcrumb', { detail: `Clubs / Activity programmes / ${action}` })
    );
    return () => {
      window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
    };
  }, [isEditMode]);

  React.useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty || isSaving) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty, isSaving]);

  const navigateBack = React.useCallback(
    (savedProgrammeId?: string) => {
      const params = new URLSearchParams(backLocation.search || '');
      if (savedProgrammeId) params.set('selectedProgrammeId', savedProgrammeId);
      const search = params.toString();
      history.push({ pathname: backLocation.pathname, search: search ? `?${search}` : '' });
    },
    [backLocation, history]
  );

  const requestBack = () => {
    if (isDirty && !isSaving) {
      setIsDiscardOpen(true);
    } else {
      navigateBack();
    }
  };

  const onSubmit = async (values: ClubProgrammeFormValues): Promise<void> => {
    setBackendOpensError('');
    setBackendClosesError('');
    setBackendFormError('');
    setIsSaving(true);

    try {
      let savedProgramme: AfterSchoolClubProgramme;
      if (isEditMode && programme && programmeId) {
        const patch = buildUpdatePayload(programme, values);
        savedProgramme = Object.keys(patch).length
          ? await updateClubProgramme(user, patch, String(programmeId))
          : programme;
      } else {
        savedProgramme = await createClubProgramme(user, buildCreatePayload(values));
      }

      const savedProgrammeId = String((savedProgramme as any)?.id || (savedProgramme as any)?._id || programmeId || '');
      setIsDirty(false);
      setIsSaving(false);
      navigateBack(savedProgrammeId);
    } catch (error) {
      console.error('ClubProgrammeEditorPage save error:', error);
      const errorText =
        propz.get(error, ['response', 'data', 'error']) ||
        propz.get(error, ['response', 'data', 'details', 'text']) ||
        propz.get(error, ['message']) ||
        'Failed to save programme.';
      const opensLocked = errorText === 'booking.opensAt cannot be changed after booking has opened';
      const closesLocked = errorText === 'booking.closesAt cannot be changed after booking has closed';
      setBackendOpensError(
        opensLocked ? 'Booking start can no longer be changed because booking has already opened.' : ''
      );
      setBackendClosesError(
        closesLocked ? 'Booking end can no longer be changed because booking has already closed.' : ''
      );
      setBackendFormError(!opensLocked && !closesLocked ? String(errorText) : '');
      setIsSaving(false);
    }
  };

  if (isLoading) return <Loader />;

  if (loadError || (isEditMode && !programme)) {
    return (
      <div className="cpEditorPage">
        <header className="cpEditorPageHeader">
          <button type="button" onClick={() => navigateBack()}>
            <span aria-hidden="true">‹</span> Back to programmes
          </button>
          <i />
          <div>
            <h1>Programme unavailable</h1>
            <p>{loadError || 'The programme could not be found.'}</p>
          </div>
        </header>
      </div>
    );
  }

  const status = String(programme?.status || 'DRAFT').toLowerCase();

  return (
    <div className="cpEditorPage">
      <header className="cpEditorPageHeader">
        <button type="button" onClick={requestBack}>
          <span aria-hidden="true">‹</span> Back to programmes
        </button>
        <i />
        <div>
          <h1>{isEditMode ? 'Edit activity programme' : 'Create activity programme'}</h1>
          <p>Set the booking workflow, visibility and audience before adding clubs.</p>
        </div>
        <span className={`cpEditorStatus is-${status}`}>{String(programme?.status || 'DRAFT')}</span>
      </header>

      <main className="cpEditorPageBody">
        <ClubProgrammeForm
          user={user}
          forms={forms}
          initial={programme}
          onCancel={requestBack}
          onSubmit={onSubmit}
          submitText={isEditMode ? 'Save changes' : 'Create programme'}
          backendOpensError={backendOpensError}
          backendClosesError={backendClosesError}
          backendFormError={backendFormError}
          isSaving={isSaving}
          onDirtyChange={setIsDirty}
        />
      </main>

      <UnsavedChangesModal
        isOpen={isDiscardOpen}
        message="You have unsaved changes to this activity programme. If you leave now, those changes will be lost."
        onKeepEditing={() => setIsDiscardOpen(false)}
        onDiscard={() => {
          setIsDiscardOpen(false);
          setIsDirty(false);
          navigateBack();
        }}
      />
    </div>
  );
};

ClubProgrammeEditorPage.displayName = 'ClubProgrammeEditorPage';
